import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Upload, CheckCircle, AlertTriangle } from 'lucide-react';

interface ParsedRow {
  city_ascii: string;
  state_id: string;
  zips?: string;
  lat?: string;
  lng?: string;
  population?: string;
  county_name?: string;
  state_name?: string;
}

// Known header aliases
const HEADER_MAP: Record<string, keyof ParsedRow> = {
  city_ascii: 'city_ascii',
  city: 'city_ascii',
  state_id: 'state_id',
  state_code: 'state_id',
  zips: 'zips',
  lat: 'lat',
  latitude: 'lat',
  lng: 'lng',
  lon: 'lng',
  longitude: 'lng',
  population: 'population',
  county_name: 'county_name',
  county: 'county_name',
  county_fips: 'county_name', // fallback
  state_name: 'state_name',
};

const AdminCitiesImport: React.FC = () => {
  const [csvData, setCsvData] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [clearExisting, setClearExisting] = useState(true);
  const [progress, setProgress] = useState({ inserted: 0, total: 0, batch: 0, totalBatches: 0 });
  const [isDone, setIsDone] = useState(false);

  const parseCsv = (raw: string): { headers: string[]; mappedHeaders: Record<string, keyof ParsedRow>; rows: ParsedRow[] } => {
    const lines = raw.trim().split('\n');
    if (lines.length === 0) return { headers: [], mappedHeaders: {}, rows: [] };

    // Detect delimiter
    const firstLine = lines[0];
    const delimiter = firstLine.includes('\t') ? '\t' : ',';

    // Parse a single line respecting quotes
    const parseLine = (line: string): string[] => {
      if (delimiter === '\t') return line.split('\t').map(s => s.trim());
      const parts: string[] = [];
      let current = '';
      let inQuotes = false;
      for (const ch of line) {
        if (ch === '"') {
          inQuotes = !inQuotes;
        } else if (ch === ',' && !inQuotes) {
          parts.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
      parts.push(current.trim());
      return parts;
    };

    // Parse header row
    const rawHeaders = parseLine(lines[0]).map(h => h.replace(/^"|"$/g, '').toLowerCase().trim());

    // Map headers to known fields
    const mappedHeaders: Record<string, keyof ParsedRow> = {};
    const colIndices: { index: number; field: keyof ParsedRow }[] = [];

    rawHeaders.forEach((h, i) => {
      const mapped = HEADER_MAP[h];
      if (mapped) {
        mappedHeaders[h] = mapped;
        colIndices.push({ index: i, field: mapped });
      }
    });

    // Must have at least city and state
    const hasCity = colIndices.some(c => c.field === 'city_ascii');
    const hasState = colIndices.some(c => c.field === 'state_id');

    if (!hasCity || !hasState) {
      return { headers: rawHeaders, mappedHeaders, rows: [] };
    }

    // Parse data rows
    const rows: ParsedRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const trimmed = lines[i].trim();
      if (!trimmed) continue;
      const parts = parseLine(trimmed);
      const row: Partial<ParsedRow> = {};
      for (const { index, field } of colIndices) {
        if (index < parts.length) {
          (row as any)[field] = parts[index].replace(/^"|"$/g, '');
        }
      }
      if (row.city_ascii && row.state_id) {
        rows.push(row as ParsedRow);
      }
    }

    return { headers: rawHeaders, mappedHeaders, rows };
  };

  const parsed = useMemo(() => {
    if (!csvData.trim()) return null;
    return parseCsv(csvData);
  }, [csvData]);

  const handleImport = async () => {
    if (!parsed || parsed.rows.length === 0) {
      toast.error('No valid rows found. Ensure CSV has city_ascii and state_id columns.');
      return;
    }

    setIsImporting(true);
    setIsDone(false);

    const batchSize = 1000;
    const totalBatches = Math.ceil(parsed.rows.length / batchSize);
    let totalInserted = 0;
    let errors: string[] = [];

    for (let i = 0; i < parsed.rows.length; i += batchSize) {
      const batch = parsed.rows.slice(i, i + batchSize);
      const batchNum = Math.floor(i / batchSize) + 1;
      setProgress({ inserted: totalInserted, total: parsed.rows.length, batch: batchNum, totalBatches });

      try {
        const { data, error } = await supabase.functions.invoke('import-cities', {
          body: {
            rows: batch,
            // Only send truncate on first batch if toggle is on
            ...(i === 0 && clearExisting ? { truncate: true } : {}),
          },
        });

        if (error) {
          errors.push(`Batch ${batchNum}: ${error.message}`);
        } else if (data?.inserted) {
          totalInserted += data.inserted;
        }
        if (data?.errors?.length) {
          errors.push(...data.errors);
        }
      } catch (err) {
        errors.push(`Batch ${batchNum}: ${String(err)}`);
      }
    }

    setProgress({ inserted: totalInserted, total: parsed.rows.length, batch: totalBatches, totalBatches });
    setIsImporting(false);
    setIsDone(true);

    if (errors.length > 0) {
      toast.error(`Import completed with ${errors.length} error(s): ${errors.slice(0, 3).join('; ')}`);
    } else {
      toast.success(`Successfully imported ${totalInserted.toLocaleString()} cities!`);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCsvData(evt.target?.result as string);
      setIsDone(false);
    };
    reader.readAsText(file);
  };

  const detectedFields = parsed ? Object.entries(parsed.mappedHeaders) : [];

  return (
    <div className="max-w-3xl mx-auto p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Cities Data
          </CardTitle>
          <CardDescription>
            Upload your CSV file with US cities. Headers are auto-detected by name. 
            Required columns: <code>city_ascii</code>, <code>state_id</code>. 
            Optional: <code>lat</code>, <code>lng</code>, <code>population</code>, <code>zips</code>, <code>county_name</code>, <code>state_name</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Upload CSV file</label>
            <input
              type="file"
              accept=".csv,.tsv,.txt"
              onChange={handleFileUpload}
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Or paste CSV data</label>
            <Textarea
              value={csvData}
              onChange={(e) => { setCsvData(e.target.value); setIsDone(false); }}
              placeholder={`city_ascii,state_id,lat,lng,population,zips,county_name,state_name\nNew York,NY,40.7128,-74.0060,8336817,10001 10002,New York,New York`}
              rows={8}
              className="font-mono text-xs"
            />
          </div>

          {/* Column detection preview */}
          {parsed && (
            <div className="rounded-md border p-3 space-y-2">
              <h4 className="text-sm font-medium">Detected Columns</h4>
              {detectedFields.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {detectedFields.map(([csv, mapped]) => (
                    <span key={csv} className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-xs font-medium">
                      {csv} → {mapped}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  Could not detect required columns (city_ascii, state_id). Check your CSV headers.
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {parsed.rows.length.toLocaleString()} data rows detected
              </p>
            </div>
          )}

          {/* Clear existing toggle */}
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Clear existing data first</p>
              <p className="text-xs text-muted-foreground">Recommended for full re-imports. Deletes all current cities before importing.</p>
            </div>
            <Switch checked={clearExisting} onCheckedChange={setClearExisting} />
          </div>

          {isImporting && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Importing batch {progress.batch} of {progress.totalBatches}... ({progress.inserted.toLocaleString()} inserted so far)
            </div>
          )}

          {isDone && (
            <div className="flex items-center gap-2 text-sm text-primary">
              <CheckCircle className="h-4 w-4" />
              Done! {progress.inserted.toLocaleString()} of {progress.total.toLocaleString()} cities imported.
            </div>
          )}

          <Button
            onClick={handleImport}
            disabled={isImporting || !parsed || parsed.rows.length === 0}
            className="w-full"
          >
            {isImporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Importing...
              </>
            ) : (
              `Import ${parsed?.rows.length.toLocaleString() || 0} Cities`
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCitiesImport;
